import type { SortingState } from '@tanstack/react-table';

/**
 * Props for the DataTable.Sorting configuration component.
 *
 * Supports two modes:
 * - **Controlled**: Use `value` + `onChange` - parent owns the state
 * - **Uncontrolled**: Use `initial` + optional `onChange` - table owns the state
 */
export interface SortingProps {
  /**
   * Controlled mode: Current sorting state.
   * When provided, the table syncs to this value on every render.
   * Parent is the source of truth.
   *
   * @example
   * ```tsx
   * const [sorting, setSorting] = useState<SortingState>([]);
   * <DataTable.Sorting value={sorting} onChange={setSorting} />
   * ```
   */
  value?: SortingState;

  /**
   * Uncontrolled mode: Initial sorting state applied on mount.
   * Only used when `value` is not provided.
   * Takes priority over cached sorting state.
   *
   * @example
   * ```tsx
   * <DataTable.Sorting initial={[{ id: 'date', desc: true }]} />
   * ```
   */
  initial?: SortingState;

  /**
   * Called when sorting changes from any source (user interaction or programmatic).
   * Works in both controlled and uncontrolled modes.
   *
   * @example
   * ```tsx
   * <DataTable.Sorting onChange={(sorting) => console.log('Sorted by:', sorting)} />
   * ```
   */
  onChange?: (sorting: SortingState) => void;
}

/**
 * Configuration component for external sorting state management.
 *
 * This is a declarative config component that returns null.
 * It is parsed by the table-config-parser to extract sorting configuration.
 *
 * @example Uncontrolled with initial sorting
 * ```tsx
 * <DataTable id="users" data={users}>
 *   <DataTable.Sorting initial={[{ id: 'name', desc: false }]} />
 *   <DataTable.Column id="name" accessor="name" sortable />
 * </DataTable>
 * ```
 *
 * @example Controlled mode (parent owns state)
 * ```tsx
 * const [sorting, setSorting] = useState<SortingState>([]);
 *
 * <DataTable id="users" data={users}>
 *   <DataTable.Sorting value={sorting} onChange={setSorting} />
 *   <DataTable.Column id="name" accessor="name" sortable />
 * </DataTable>
 * ```
 *
 * @example URL sync (controlled)
 * ```tsx
 * const sorting = parseSortingFromUrl(searchParams);
 *
 * <DataTable id="users" data={users}>
 *   <DataTable.Sorting
 *     value={sorting}
 *     onChange={(s) => setSearchParams({ sort: s[0]?.id, desc: s[0]?.desc })}
 *   />
 * </DataTable>
 * ```
 */
export const Sorting = (props: SortingProps): null => {
  if (import.meta.env.DEV) {
    if (props.value !== undefined && props.initial !== undefined) {
      console.warn(
        'DataTable.Sorting: `value` and `initial` are mutually exclusive. ' +
          'Use `value` for controlled mode or `initial` for uncontrolled mode, not both.'
      );
    }
    if (props.value !== undefined && props.onChange === undefined) {
      console.warn(
        'DataTable.Sorting: Using `value` without `onChange` makes the sorting state read-only. ' +
          'User interactions will not update sorting. Provide an `onChange` handler.'
      );
    }
  }
  return null;
};
Sorting.displayName = 'DataTable.Sorting';
